import { useEffect, useState, type FormEvent } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import {
  useCreateSubscriber, useUpdateSubscriber, type SaveSubscriberInput,
} from '@/hooks/useNewsletter';
import {
  SUBSCRIBER_STATUS_LABELS, type Subscriber, type SubscriberStatus,
} from '@/types/newsletter';

type Props = {
  open: boolean;
  onClose: () => void;
  subscriber?: Subscriber | null;
};

const EMPTY: SaveSubscriberInput = {
  email: '',
  firstName: '',
  lastName: '',
  company: '',
  status: 'active',
  locale: 'en',
};

export function SubscriberFormModal({ open, onClose, subscriber }: Props) {
  const create = useCreateSubscriber();
  const update = useUpdateSubscriber();
  const editing = !!subscriber;

  const [form, setForm] = useState<SaveSubscriberInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(subscriber ? {
        email: subscriber.email,
        firstName: subscriber.firstName ?? '',
        lastName: subscriber.lastName ?? '',
        company: subscriber.company ?? '',
        title: subscriber.title ?? '',
        phone: subscriber.phone ?? '',
        status: subscriber.status,
        locale: subscriber.locale ?? 'en',
      } : EMPTY);
      setError(null);
    }
  }, [open, subscriber]);

  function update_<K extends keyof SaveSubscriberInput>(key: K, val: SaveSubscriberInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editing && subscriber) {
        await update.mutateAsync({ id: subscriber.id, ...form });
        toast.success(`Updated ${form.email}`);
      } else {
        await create.mutateAsync(form);
        toast.success(`Added ${form.email}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Subscriber' : 'New Subscriber'}
      title={editing ? form.email : 'Add a subscriber.'}
      description={editing ? undefined : 'Manually add a single subscriber. For bulk additions, use CSV Import.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="sub-form" loading={submitting} leftIcon={<Mail size={13} />}>
            {editing ? 'Save Changes' : 'Add Subscriber'}
          </Button>
        </>
      }
    >
      <form id="sub-form" onSubmit={onSubmit} className="space-y-4 py-2">
        <Input
          label="Email"
          type="email"
          required
          placeholder="person@example.com"
          value={form.email}
          onChange={(e) => update_('email', e.target.value.toLowerCase().trim())}
          disabled={editing}
          leftIcon={<Mail size={14} />}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input label="First Name" value={form.firstName ?? ''} onChange={(e) => update_('firstName', e.target.value)} />
          <Input label="Last Name"  value={form.lastName ?? ''}  onChange={(e) => update_('lastName', e.target.value)} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input label="Company" value={form.company ?? ''} onChange={(e) => update_('company', e.target.value)} />
          <Input label="Title"   value={form.title ?? ''}   onChange={(e) => update_('title', e.target.value)} />
        </div>

        <Input label="Phone" type="tel" value={form.phone ?? ''} onChange={(e) => update_('phone', e.target.value)} />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Status"
            value={form.status ?? 'active'}
            onChange={(e) => update_('status', e.target.value as SubscriberStatus)}
            options={(['active', 'pending', 'unsubscribed'] as SubscriberStatus[]).map((s) => ({
              value: s, label: SUBSCRIBER_STATUS_LABELS[s],
            }))}
          />
          <Select
            label="Language"
            value={form.locale ?? 'en'}
            onChange={(e) => update_('locale', e.target.value)}
            options={[
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Español' },
            ]}
          />
        </div>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
